import _class_call_check from "@swc/helpers/src/_class_call_check.mjs";
import _create_class from "@swc/helpers/src/_create_class.mjs";
import _inherits from "@swc/helpers/src/_inherits.mjs";
import _create_super from "@swc/helpers/src/_create_super.mjs";
import { Check, Guard } from "@silvertours/lib-core-types";
import { Entity } from "@silvertours/lib-sys-domain";
import { StatusCode } from "./content";
import { PageAttributesOrganizer } from "./pageAttributes";
import { LegacyStage, ModernStage } from "./stage";
import { LegacyBody, ModernBody } from "./body";
var isOfStatus = function(param, statusCode) {
    var content = param.content;
    return content.statusCode === statusCode;
};
var isOk = function(memento) {
    return isOfStatus(memento, StatusCode.Ok);
};
var isRedirect = function(memento) {
    return isOfStatus(memento, StatusCode.PermanentRedirect) || isOfStatus(memento, StatusCode.TemporaryRedirect);
};
var isRetired = function(memento) {
    return isOfStatus(memento, StatusCode.Gone) || isOfStatus(memento, StatusCode.NotFound);
};
var LandingPage = /*#__PURE__*/ function(Entity) {
    "use strict";
    _inherits(LandingPage, Entity);
    var _super = _create_super(LandingPage);
    function LandingPage(memento) {
        _class_call_check(this, LandingPage);
        return _super.call(this, "LandingPage", memento);
    }
    var _proto = LandingPage.prototype;
    /**
   * @remarks
   * A page is more specific when it has a greater number of associated entities
   * than the alternative.
   */ _proto.isMoreSpecificThan = function isMoreSpecificThan(otherPage) {
        return this.attributes.length > otherPage.attributes.length;
    };
    _proto.getPropertyIfIsStatus = function getPropertyIfIsStatus(isStatus, getProperty, fallback) {
        if (isStatus(this.memento)) {
            return getProperty(this.memento);
        }
        Guard.isDefined(fallback, {
            errorMessage: "Attempt to access a property which is not available for the given status"
        });
        return fallback;
    };
    _proto.getBmcgContentIfOk = function getBmcgContentIfOk(getProperty, fallback) {
        return this.getPropertyIfIsStatus(isOk, function(param) {
            var content = param.content;
            if (content.type === "bmcg") {
                return getProperty(content);
            }
            Guard.isDefined(fallback, {
                errorMessage: "Attempt to access a property which is not available for the given status"
            });
            return fallback;
        }, fallback);
    };
    // private getFloytContentIfOk<TResult, TFallback extends TResult = TResult>(
    //   getProperty: (content: FloytOkContent) => TResult,
    //   fallback?: TFallback,
    // ) {
    //   return this.getPropertyIfIsStatus(
    //     isOk,
    //     ({ content }) => {
    //       if (content.type === 'floyt') {
    //         return getProperty(content);
    //       }
    //       Guard.isDefined(fallback, {
    //         errorMessage:
    //           'Attempt to access a property which is not available for the given status',
    //       });
    //       return fallback;
    //     },
    //     fallback,
    //   );
    // }
    _proto.getIfOk = function getIfOk(getProperty, fallback) {
        return this.getPropertyIfIsStatus(isOk, getProperty, fallback);
    };
    _proto.getIfRedirect = function getIfRedirect(getProperty) {
        return this.getPropertyIfIsStatus(isRedirect, getProperty);
    };
    LandingPage.Redirect = function Redirect(landingUrl, content) {
        return new LandingPage({
            id: landingUrl,
            content: content
        });
    };
    LandingPage.Gone = function Gone(landingUrl) {
        return new LandingPage({
            id: landingUrl,
            content: {
                landingUrl: landingUrl,
                statusCode: StatusCode.Gone
            }
        });
    };
    LandingPage.NotFound = function NotFound(landingUrl) {
        return new LandingPage({
            id: landingUrl,
            content: {
                landingUrl: landingUrl,
                statusCode: StatusCode.NotFound
            }
        });
    };
    LandingPage.Ok = function Ok(landingUrl, content, product, attributes) {
        // @todo: Validation guard clauses
        return new LandingPage({
            id: landingUrl,
            attributes: attributes,
            content: content,
            path: landingUrl,
            product: product
        });
    };
    _create_class(LandingPage, [
        {
            key: "slug",
            get: function get() {
                return this.idRequired.replace(/\..*/g, "");
            }
        },
        {
            key: "statusCode",
            get: function get() {
                return this.memento.content.statusCode;
            }
        },
        {
            key: "isOk",
            get: function get() {
                return isOk(this.memento);
            }
        },
        {
            key: "isRedirect",
            get: function get() {
                return isRedirect(this.memento);
            }
        },
        {
            key: "isRetired",
            get: function get() {
                return isRetired(this.memento);
            }
        },
        {
            key: "redirectUrl",
            get: function get() {
                return this.getIfRedirect(function(param) {
                    var content = param.content;
                    return content.redirectUrl;
                });
            }
        },
        {
            key: "alternativePaths",
            get: function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    return content.alternativePaths;
                }, []);
            }
        },
        {
            key: "headlines",
            get: function get() {
                return this.getBmcgContentIfOk(function(param) {
                    var headlines = param.headlines;
                    return headlines;
                });
            }
        },
        {
            key: "path",
            get: function get() {
                return this.getIfOk(function(param) {
                    var path = param.path;
                    return path;
                }, "");
            }
        },
        {
            key: "body",
            get: function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    if (content.type === "floyt") {
                        return new ModernBody(content);
                    }
                    return new LegacyBody();
                });
            }
        },
        {
            key: "modernBodyBeta",
            get: /**
   * @todo
   * This "beta" property is for testing the modern approach on BM/CG without
   * impacting the existing approach. Once BM/CG is ready switch over fully, it
   * can replace the `body` property so that both site groups are fully aligned.
   * */ function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    return new ModernBody(content);
                });
            }
        },
        {
            key: "meta",
            get: function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    return content.meta;
                });
            }
        },
        {
            key: "stage",
            get: function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    if (content.type === "floyt") {
                        return new ModernStage(content);
                    }
                    return new LegacyStage(content);
                });
            }
        },
        {
            key: "airport",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.airport;
                }, null);
            }
        },
        {
            key: "bookingAttribute",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.bookingAttributes;
                }, []);
            }
        },
        {
            key: "category",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.category;
                }, null);
            }
        },
        {
            key: "city",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.city;
                }, null);
            }
        },
        {
            key: "cityDistrict",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.cityDistrict;
                }, null);
            }
        },
        {
            key: "country",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.country;
                }, null);
            }
        },
        {
            key: "infoBanner",
            get: function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    return content.infoBanner;
                });
            }
        },
        {
            key: "intro",
            get: function get() {
                return this.getBmcgContentIfOk(function(param) {
                    var introModule = param.introModule;
                    return introModule;
                });
            }
        },
        {
            key: "island",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.island;
                }, null);
            }
        },
        {
            key: "partners",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.partners;
                }, []);
            }
        },
        {
            key: "railStation",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.railStation;
                }, null);
            }
        },
        {
            key: "state",
            get: function get() {
                return this.getIfOk(function(param) {
                    var attributes = param.attributes;
                    return attributes.state;
                }, null);
            }
        },
        {
            key: "navigation",
            get: function get() {
                return this.getIfOk(function(param) {
                    var content = param.content;
                    if (content.type === "bmcg") {
                        return content.navigation;
                    }
                    return {
                        // Floyt static links not yet integrated into backend architecture
                        staticLinks: []
                    };
                });
            }
        },
        {
            key: "stationsMap",
            get: function get() {
                var _this = this;
                return this.getIfOk(function(param) {
                    var content = param.content;
                    if (content.type === "bmcg") {
                        return {
                            stations: content.stations,
                            boundingBox: _this.boundingBox
                        };
                    }
                    return {
                        stations: content.stations,
                        boundingBox: null
                    };
                });
            }
        },
        {
            key: "product",
            get: function get() {
                return this.getIfOk(function(param) {
                    var product = param.product;
                    return product;
                });
            }
        },
        {
            key: "boundingBox",
            get: function get() {
                var ref, ref1, ref2, ref3;
                return ((ref = this.island) === null || ref === void 0 ? void 0 : ref.boundingBox) || ((ref1 = this.cityDistrict) === null || ref1 === void 0 ? void 0 : ref1.boundingBox) || ((ref2 = this.city) === null || ref2 === void 0 ? void 0 : ref2.boundingBox) || ((ref3 = this.state) === null || ref3 === void 0 ? void 0 : ref3.boundingBox) || null;
            }
        },
        {
            key: "locationName",
            get: function get() {
                var ref, ref1, ref2, ref3, ref4, ref5, ref6;
                return ((ref = this.city) === null || ref === void 0 ? void 0 : ref.name) || ((ref1 = this.state) === null || ref1 === void 0 ? void 0 : ref1.name) || ((ref2 = this.country) === null || ref2 === void 0 ? void 0 : ref2.name) || ((ref3 = this.island) === null || ref3 === void 0 ? void 0 : ref3.name) || ((ref4 = this.airport) === null || ref4 === void 0 ? void 0 : ref4.city) || ((ref5 = this.railStation) === null || ref5 === void 0 ? void 0 : ref5.cityName) || ((ref6 = this.cityDistrict) === null || ref6 === void 0 ? void 0 : ref6.cityName) || "";
            }
        },
        {
            key: "attributeSet",
            get: function get() {
                return this.organizer.attributes;
            }
        },
        {
            key: "attributes",
            get: function get() {
                return this.organizer.available;
            }
        },
        {
            key: "primaryAttribute",
            get: function get() {
                return this.organizer.primary;
            }
        },
        {
            key: "secondaryAttribute",
            get: function get() {
                return this.organizer.secondary;
            }
        },
        {
            key: "possibleAttributes",
            get: function get() {
                return this.organizer.ordered;
            }
        },
        {
            key: "organizer",
            get: function get() {
                if (!Check.isDefined(this._attributeOganizer)) {
                    var attributes = this.getIfOk(function(param) {
                        var attributes = param.attributes;
                        return attributes;
                    });
                    this._attributeOganizer = new PageAttributesOrganizer(attributes);
                }
                return this._attributeOganizer;
            }
        }
    ]);
    return LandingPage;
}(Entity);
export { LandingPage };
